import styled from '@emotion/styled';
import {
  DeleteOutline,
  DriveFolderUploadOutlined,
  EditOutlined,
  FormatListBulletedOutlined,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useProductPackagingDelete from 'api/productDetail/useProductPackagingDelete/useProductPackagingDelete';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import ProductPackagingEditModal from 'components/shared/Modals/productsList/ProductPackagingEditModal/ProductPackagingEditModal';
import ProductPackagingInsertModal from 'components/shared/Modals/productsList/ProductPackagingInsertModal/ProductPackagingInsertModal';
import ProductPrintSettingsModal from 'components/shared/Modals/productsList/ProductPrintSettingsModal/ProductPrintSettingsModal';
import PackagePrintModal from 'components/shared/productDetail/PrintPackaging';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import { useCallback, useMemo, useState } from 'react';

const getPackId = packItem => packItem?.guid || packItem?.id;
const getPackTitle = packItem => packItem?.display || packItem?.title;
const getMotherPack = packItem =>
  packItem?.parentDisplay || packItem?.motherPack;

const packagingPermissions =
  permissions.general.categories.globalSettings.productManagement.productList
    .detail.packaging;

const BranchPackagingList = ({
  packagingList,
  branchGuid,
  refreshBranchData,
}) => {
  const { can } = usePermissions();
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteChosenLocation, setDeleteChosenLocation] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPrintSettingModalOpen, setIsPrintSettingModalOpen] = useState(false);
  const [editChosenProduct, setEditChosenProduct] = useState({});
  const [printSettingChosenProduct, setPrintSettingChosenProduct] = useState(
    {},
  );
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [chosenPrintProduct, setChosenPrintProduct] = useState({});

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowInfo, setSelectedRowInfo] = useState(null);

  const openMenu = Boolean(anchorEl);

  const handleClickMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowInfo(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRowInfo(null);
  };

  const [deleteRequest] = useProductPackagingDelete();

  const print = useCallback(
    row => () => {
      setChosenPrintProduct(row);
      setIsPrintModalOpen(true);
    },
    [],
  );

  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenLocation(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const editItem = useCallback(
    row => () => {
      setEditChosenProduct(row);
      setIsEditModalOpen(true);
    },
    [],
  );

  const printSettingItem = useCallback(
    row => () => {
      setPrintSettingChosenProduct(row);
      setIsPrintSettingModalOpen(true);
    },
    [],
  );

  const deleteHandle = id => {
    setDeleteLoading(true);
    deleteRequest(
      id,
      refreshBranchData,
      setIsDeleteModalOpen,
      setDeleteLoading,
      refreshBranchData,
    );
  };

  const menuItems = useMemo(
    () => [
      {
        label: 'حذف',
        permission: packagingPermissions.delete,
        icon: DeleteOutline,
        color: 'error',
        onClick: row => {
          handleCloseMenu();
          deleteItem(row)();
        },
      },
      {
        label: 'ویرایش',
        permission: packagingPermissions.edit,
        icon: EditOutlined,
        color: 'warning',
        onClick: row => {
          handleCloseMenu();
          editItem(row)();
        },
      },
      {
        label: 'تنظیمات چاپ',
        permission: packagingPermissions.printSettings,
        icon: EditOutlined,
        color: 'warning',
        onClick: row => {
          handleCloseMenu();
          printSettingItem(row)();
        },
      },
      {
        label: 'آپلود فایل سریال',
        permission: packagingPermissions.uploadSerialFile,
        icon: DriveFolderUploadOutlined,
        color: 'success',
        onClick: row => {
          handleCloseMenu();
          print(row)();
        },
      },
      {
        label: 'لیست سریال های فعال',
        permission: packagingPermissions.activeSerialList,
        icon: FormatListBulletedOutlined,
        color: 'info',
        onClick: row => {
          handleCloseMenu();
          print(row)();
        },
      },
    ],
    [deleteItem, editItem, printSettingItem, print],
  );

  const visibleMenuItems = useMemo(
    () => menuItems.filter(item => can(item.permission)),
    [menuItems, can],
  );

  const canPrint = can(packagingPermissions.print);
  const canCreate = can(packagingPermissions.create);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 4,
          mb: 2,
          paddingX: '16px',
        }}
      >
        <Typography variant="h6" fontSize={18} fontWeight={600}>
          لیست پکیج‌ ها
        </Typography>
        {canCreate ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setIsInsertModalOpen(true)}
          >
            <AddIcon />
            افزودن پکیج
          </Button>
        ) : null}
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  borderColor: 'rgba(100, 100, 100, .2)',
                  background: 'rgba(180, 180, 180, 0.05)',
                },
              }}
            >
              <TableCell
                align="right"
                sx={{ width: { xs: 'auto', lg: '450px', xl: '550px' } }}
              >
                نام
              </TableCell>
              <TableCell align="right">بسته‌‌ بندی مادر</TableCell>
              <TableCell align="right">تعداد</TableCell>
              <TableCell align="left" width={100}>
                عملیات ها
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {packagingList?.map((packItem, index) => {
              const packTitle = getPackTitle(packItem);

              return (
                <TableRow key={getPackId(packItem) || index}>
                  <TableCell align="right">{packTitle}</TableCell>
                  <TableCell align="right">{getMotherPack(packItem)}</TableCell>
                  <TableCell align="right">{packItem.count}</TableCell>
                  <TableCell align="right" width={180}>
                    <ActionContainer>
                      {canPrint ? (
                        <Button
                          variant="contained"
                          color="info"
                          size="small"
                          onClick={() => {
                            handleCloseMenu();
                            print(packItem)();
                          }}
                        >
                          پرینت
                        </Button>
                      ) : null}
                      {visibleMenuItems.length > 0 ? (
                        <div>
                          <IconButton
                            id={`${packTitle}-button`}
                            aria-controls={
                              openMenu ? `${packTitle}-menu` : undefined
                            }
                            aria-haspopup="true"
                            aria-expanded={openMenu ? 'true' : undefined}
                            onClick={e => handleClickMenu(e, packItem)}
                          >
                            <MenuIcon />
                          </IconButton>
                        </div>
                      ) : null}
                    </ActionContainer>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {visibleMenuItems.map(item => {
            const Icon = item.icon;

            return (
              <MenuItem
                key={item.label}
                onClick={() => item.onClick(selectedRowInfo)}
              >
                <Action>
                  <Typography fontSize={14}>{item.label}</Typography>
                  <Icon fontSize="small" color={item.color} />
                </Action>
              </MenuItem>
            );
          })}
        </Menu>
      </TableContainer>

      {canCreate && isInsertModalOpen ? (
        <ProductPackagingInsertModal
          open={isInsertModalOpen}
          branchGuid={branchGuid}
          refetchPackagingList={refreshBranchData}
          handleClose={() => setIsInsertModalOpen(false)}
        />
      ) : null}

      {can(packagingPermissions.edit) && isEditModalOpen ? (
        <ProductPackagingEditModal
          open={isEditModalOpen}
          branchGuid={branchGuid}
          packGuid={getPackId(editChosenProduct)}
          refetchPackagingList={refreshBranchData}
          handleClose={() => setIsEditModalOpen(false)}
        />
      ) : null}

      {can(packagingPermissions.delete) ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={() => setIsDeleteModalOpen(false)}
          title={getPackTitle(deleteChosenLocation)}
          locationId={getPackId(deleteChosenLocation)}
          deleteLoading={deleteLoading}
          onDelete={deleteHandle}
        />
      ) : null}

      {can(packagingPermissions.printSettings) && isPrintSettingModalOpen ? (
        <ProductPrintSettingsModal
          open={isPrintSettingModalOpen}
          branchGuid={branchGuid}
          packGuid={getPackId(printSettingChosenProduct)}
          handleClose={() => setIsPrintSettingModalOpen(false)}
        />
      ) : null}

      {canPrint ? (
        <PackagePrintModal
          open={isPrintModalOpen}
          setOpen={() => setIsPrintModalOpen(false)}
          guid={getPackId(chosenPrintProduct)}
        />
      ) : null}
    </Box>
  );
};

export default BranchPackagingList;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`;

const Action = styled(Box)({
  paddingTop: '0.3rem',
  paddingBottom: '0.3rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem',
});
